// File: src/pages/PageMenu/PurchasesMenu.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Typography,
  ButtonBase,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// أيقونات Material-UI المستوردة
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GroupIcon from "@mui/icons-material/Group";
import BarChartIcon from "@mui/icons-material/BarChart";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PaymentIcon from "@mui/icons-material/Payment";

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function PurchasesMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRTL = i18n.language === 'ar';
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? Number(storedCompanyId) : null;

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setError(t('purchasesMenu.companyIdNotFound'));
      return;
    }
    setLoading(true);
    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then((res) => setInvoiceTypes(res.data || []))
      .catch((err) => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        setError(t('purchasesMenu.failedToLoadInvoiceTypes'));
      })
      .finally(() => setLoading(false));
  }, [companyId, t]);

  // دالة مساعدة لتحديد الأيقونة واللون المخصص لكل نوع فاتورة ديناميكية
  const getPurchaseTypeIconConfig = (name) => {
    const trimmedName = name.trim();
    if (["شراء", "فاتورة شراء"].includes(trimmedName)) {
      return { icon: <ReceiptLongIcon />, color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.06)" };
    } else if (["مرتجع مشتريات", "فاتورة مرتجع مشتريات"].includes(trimmedName)) {
      return { icon: <ReplayIcon />, color: "#F59E0B", bgColor: "rgba(245, 158, 11, 0.06)" };
    }
    return { icon: <ReceiptLongIcon />, color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.06)" };
  };

  // دالة عرض الأيقونات المطورة لتكون 4 عناصر كحد أقصى في الصف مرنة لجميع الشاشات
  const renderGridItems = (items) => {
    return (
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',          // جوال: كرتين بالصف لسهولة النقر
            sm: 'repeat(3, 1fr)',          // تابلت: 3 كروت بالصف
            md: 'repeat(4, 1fr)',          // لابتوب وشاشات متوسطة: 4 كروت كحد أقصى
            lg: 'repeat(4, 1fr)',          // شاشات مكتبية عريضة: تثبيت 4 كروت بالصف
          },
          width: '100%'
        }}
      >
        {items.map((item, index) => (
          <ButtonBase
            key={index}
            onClick={() => navigate(item.link)}
            component={Paper}
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 3, sm: 4 },
              borderRadius: '24px', // حواف ناعمة جداً عصرية
              backgroundColor: "#ffffff",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.04)',
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: `0 20px 25px -5px ${item.bgColor}, 0 10px 10px -5px rgba(0, 0, 0, 0.01)`,
                borderColor: item.color,
                "& .icon-container": {
                  transform: 'scale(1.08) rotate(3deg)',
                  backgroundColor: item.color,
                  color: '#ffffff'
                }
              },
              "&:active": { transform: "translateY(-1px)" }
            }}
          >
            {/* حاوية الأيقونة الدائرية التفاعلية */}
            <Box
              className="icon-container"
              sx={{
                width: { xs: 55, sm: 65 },
                height: { xs: 55, sm: 65 },
                borderRadius: '18px',
                backgroundColor: item.bgColor,
                color: item.color,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                transition: 'all 0.3s ease',
                '& svg': { fontSize: { xs: 26, sm: 30 } }
              }}
            >
              {item.icon}
            </Box>

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                color: '#1E293B',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                letterSpacing: '-0.3px',
                lineHeight: 1.4,
                px: 0.5
              }}
            >
              {item.name}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    );
  };

  // جلب وتصفية فواتير المشتريات وتطبيق الألوان المتناسقة
  const purchaseInvoiceOptions = invoiceTypes
    .filter((type) => {
        const name = type.name.trim();
        const isPurchase = ["شراء", "فاتورة شراء"].includes(name);
        const isPurchaseReturn = ["مرتجع مشتريات", "فاتورة مرتجع مشتريات"].includes(name);

        if (isPurchase) return hasPermission(PERMISSIONS.INVOICE_PURCHASE_CREATE);
        if (isPurchaseReturn) return hasPermission(PERMISSIONS.INVOICE_PURCHASE_RETURN_CREATE);

        return false;
    })
    .map((type) => {
      let translationKey = '';
      switch (type.name.trim()) {
        case "شراء":
        case "فاتورة شراء":
          translationKey = 'purchasesMenu.purchaseInvoice';
          break;
        case "مرتجع مشتريات":
        case "فاتورة مرتجع مشتريات":
          translationKey = 'purchasesMenu.purchaseReturn';
          break;
        default:
          translationKey = 'purchasesMenu.purchaseInvoice';
      }
      
      const config = getPurchaseTypeIconConfig(type.name);

      return {
        name: t(translationKey),
        icon: config.icon,
        color: config.color,
        bgColor: config.bgColor,
        link: `/invoices/new/${type.invoiceTypeId}`,
      };
    });

  // إعداد أقسام القائمة بألوان ناعمة متوافقة مع روح الواجهة
  const invoiceSection = [
    ...purchaseInvoiceOptions,
    hasPermission(PERMISSIONS.VOUCHER_CREATE) && { 
      name: t('purchasesMenu.supplierPayment'), 
      icon: <PaymentIcon />, 
      color: "#DC2626", 
      bgColor: "rgba(220, 38, 38, 0.06)",
      link: "/vouchers/supplier-payment" 
    },
    hasPermission(PERMISSIONS.SUPPLIER_CREATE) && { 
      name: t('purchasesMenu.addSupplier'), 
      icon: <LocalShippingIcon />, 
      color: "#4F46E5", 
      bgColor: "rgba(79, 70, 229, 0.06)",
      link: "/Supplier/new" 
    },
    hasPermission(PERMISSIONS.SUPPLIER_VIEW) && { 
      name: t('purchasesMenu.suppliersList'), 
      icon: <GroupIcon />, 
      color: "#6366F1", 
      bgColor: "rgba(99, 102, 241, 0.06)",
      link: "/Supplier" 
    },
  ].filter(Boolean);

  const reportSection = [
    hasPermission(PERMISSIONS.INVOICE_PURCHASE_VIEW) && {
      name: t('purchasesMenu.allPurchaseInvoices'),
      icon: <ListAltIcon />,
      color: "#2563EB",
      bgColor: "rgba(37, 99, 235, 0.06)",
      link: "/reports/all-invoices?direction=1&invoiceTypeId=2",
    },
    hasPermission(PERMISSIONS.INVOICE_PURCHASE_RETURN_VIEW) && {
      name: t('purchasesMenu.allPurchaseReturns'),
      icon: <ListAltIcon />,
      color: "#3B82F6",
      bgColor: "rgba(59, 130, 246, 0.06)",
      link: "/reports/all-invoices?direction=1&invoiceTypeId=4",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('purchasesMenu.aggregatedItemsPurchaseReport'),
      icon: <SummarizeIcon />,
      color: "#0D9488",
      bgColor: "rgba(13, 148, 136, 0.06)",
      link: "/reports/aggregated-purchase-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('purchasesMenu.detailedItemsPurchaseReport'),
      icon: <BarChartIcon />,
      color: "#10B981",
      bgColor: "rgba(16, 185, 129, 0.06)",
      link: "/reports/detailed-purchase-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('purchasesMenu.dailyPurchaseReport'),
      icon: <CalendarTodayIcon />,
      color: "#059669",
      bgColor: "rgba(5, 150, 105, 0.06)",
      link: "/reports/daily-purchases",
    },
  ].filter(Boolean);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={45} thickness={4.5} sx={{ color: 'primary.main' }} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}>
          {t('purchasesMenu.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Paper sx={{ p: 3, borderRadius: '16px', textAlign: 'center', border: '1px solid rgba(220, 38, 38, 0.15)', bgcolor: 'rgba(220, 38, 38, 0.02)' }}>
          <Typography color="error" variant="body1" fontWeight={600}>
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 5, 
        mb: 5, 
        px: { xs: 2, md: 4 },
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* هيدر تدرج النصوص الفاخر العصري */}
      <Box sx={{ mb: 6, textAlign: { xs: 'center', md: 'start' } }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          sx={{
            background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            letterSpacing: '-0.5px',
            mb: 1
          }}
        >
          {t('purchasesMenu.dashboardTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
        </Typography>
      </Box>

      {/* قسم فواتير المشتريات */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          🛍️ {t('purchasesMenu.invoicesSection')}
        </Typography>
        {renderGridItems(invoiceSection)}
      </Box>
      
      {/* قسم تقارير المشتريات */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          📊 {t('purchasesMenu.reportsSection')}
        </Typography>
        {renderGridItems(reportSection)}
      </Box>
    </Container>
  );
}